import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { BaseButton, useT } from '@repo/common';
import { searchUniversities } from '../../../api/pathfinder/pathfinder.api';
import { ReactComponent as SearchIcon } from '@repo/common/assets/icons/20px/icon_20px_search.svg';
import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/20px/icon_20px_info_filled.svg';
import { ReactComponent as CancelIcon } from '@repo/common/assets/icons/16px/icon_16px_cancel.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/16px/icon_16px_plus_outline.svg';

const MAX_SLOTS = 3;

function PathfinderUniversitySlotSelect({
  value = [],
  onChange,
  placeholder,
}) {
  const t = useT();
  const resolvedPlaceholder =
    placeholder ??
    t(
      'Search and add your target school (e.g. Yonsei University)',
      '목표 학교를 검색해 추가해 주세요 (예: Yonsei University)',
    );
  const selected = useMemo(
    () => (Array.isArray(value) ? value.slice(0, MAX_SLOTS) : []),
    [value],
  );

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [showLimitFeedback, setShowLimitFeedback] = useState(false);

  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const closeTimerRef = useRef(null);
  const shakeTimerRef = useRef(null);

  const openPanel = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    setIsOpen(true);
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const closePanel = useCallback(() => {
    setVisible(false);
    closeTimerRef.current = setTimeout(() => setIsOpen(false), 200);
  }, []);

  useEffect(() => {
    const handleMouseDown = (event) => {
      if (!rootRef.current?.contains(event.target)) closePanel();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      clearTimeout(closeTimerRef.current);
      clearTimeout(debounceRef.current);
      clearTimeout(shakeTimerRef.current);
    };
  }, [closePanel]);

  const triggerShake = useCallback(() => {
    setShowLimitFeedback(true);
    setShaking(false);
    // 한 프레임 쉬고 다시 켜야 animation이 재트리거됨
    requestAnimationFrame(() => {
      setShaking(true);
      clearTimeout(shakeTimerRef.current);
      shakeTimerRef.current = setTimeout(() => setShaking(false), 320);
    });
  }, []);

  const handleQueryChange = useCallback(
    (event) => {
      const nextQuery = event.target.value;
      setQuery(nextQuery);
      if (!isOpen) openPanel();
      clearTimeout(debounceRef.current);
      if (!nextQuery || nextQuery.trim().length < 1) {
        setSuggestions([]);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        try {
          const results = await searchUniversities(nextQuery.trim());
          setSuggestions(
            results.map((university) => ({
              code:
                university.schoolCode ||
                university.schoolNameEn ||
                university.schoolNameKo,
              label: university.schoolNameEn
                ? `${university.schoolNameKo} (${university.schoolNameEn})`
                : university.schoolNameKo,
            })),
          );
        } catch {
          setSuggestions([]);
        }
      }, 300);
    },
    [isOpen, openPanel],
  );

  const emitSelected = useCallback(
    (nextSelected) => {
      onChange(nextSelected, {
        selectedOptions: nextSelected.map((label) => ({
          value: label,
          label,
        })),
      });
    },
    [onChange],
  );

  const handleAdd = useCallback(
    (label) => {
      if (selected.includes(label)) return;
      if (selected.length >= MAX_SLOTS) {
        triggerShake();
        return;
      }
      emitSelected([...selected, label]);
      setShowLimitFeedback(false);
      setQuery('');
      setSuggestions([]);
      inputRef.current?.focus();
    },
    [emitSelected, selected, triggerShake],
  );

  const handleRemove = useCallback(
    (label) => {
      emitSelected(selected.filter((item) => item !== label));
      setShowLimitFeedback(false);
      openPanel();
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    },
    [emitSelected, openPanel, selected],
  );

  const selectedSet = new Set(selected);
  const trimmedQuery = query.trim();
  const unselected = suggestions.filter((item) => !selectedSet.has(item.label));
  const showAddAsSchool =
    trimmedQuery.length > 0 && !selectedSet.has(trimmedQuery);
  // full 상태에서도 패널 열림 유지 (shake 피드백 제공)
  const showPanel =
    isOpen && (selected.length > 0 || unselected.length > 0 || showAddAsSchool);
  const showClear = Boolean(query || selected.length > 0);

  const handleClearQuery = useCallback(() => {
    setQuery('');
    setSuggestions([]);
    inputRef.current?.focus();
  }, []);

  return (
    <div ref={rootRef} className='flex w-full flex-col gap-4'>
      {/* ── Slot chips row (shake target) ── */}
      <div className='flex flex-col items-center gap-4'>
        <div
          className={twMerge(
            'flex items-center justify-center gap-4',
            shaking && 'pathfinder-limit-shake',
          )}
        >
          {Array.from({ length: MAX_SLOTS }).map((_, index) => {
            const label = selected[index];
            if (label) {
              return (
                <BaseButton
                  key={index}
                  type='text'
                  size='m'
                  htmlType='button'
                  typo={false}
                  isStatic
                  leftalign
                  gap='gap-2'
                  iconPosition='after'
                  icon={
                    <CancelIcon className='size-4 shrink-0 text-grayscale-gray01' />
                  }
                  onClick={() => handleRemove(label)}
                  textWrapperClass='h-auto min-w-0 flex-1 truncate text-[12px] font-medium leading-[1.4] text-grayscale-gray01'
                  className='h-[50px] max-w-[240px] min-w-0 shrink-0 cursor-pointer items-center gap-2 rounded-[100px] border-0 bg-grayscale-gray07 py-2 pl-4 pr-3 transition-opacity hover:opacity-80'
                >
                  {label}
                </BaseButton>
              );
            }
            return (
              <BaseButton
                key={index}
                type='text'
                size='m'
                htmlType='button'
                typo={false}
                isStatic
                gap='gap-2'
                icon={<PlusIcon className='size-4 text-grayscale-gray03' />}
                onClick={() => inputRef.current?.focus()}
                textWrapperClass='h-auto flex-none text-[14px] font-medium leading-[1.4] text-grayscale-gray03'
                className='h-[50px] w-[140px] min-w-0 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-[100px] border border-dashed border-grayscale-gray03 bg-grayscale-gray01 transition-colors hover:border-grayscale-gray04'
              >
                {t('Add', '추가')}
              </BaseButton>
            );
          })}
        </div>

        {/* ── Limit reached message ── */}
        {showLimitFeedback && (
          <div className='flex items-center gap-[2px]'>
            <InfoIcon className='size-5 shrink-0 text-text-failed' />
            <span className='text-[14px] font-medium leading-[1.4] text-text-failed'>
              {t('You can select up to 3 experiences', '최대 3개까지 선택할 수 있어요')}
            </span>
          </div>
        )}
      </div>

      {/* ── Search bar + dropdown ── */}
      <div className='flex w-full flex-col gap-[6px]'>
        <div className='flex h-[80px] w-full flex-col items-start justify-center'>
          <div
            className={twMerge(
              'group flex h-full w-full items-center gap-2 rounded-[15px] border-2 bg-surface-lightgray60 py-[10px] pl-4 pr-3 transition-colors duration-200',
              isOpen ? 'border-surface-green40' : 'border-transparent',
            )}
            onMouseDown={(event) => {
              if (event.target.closest('button')) return;
              event.preventDefault();
              inputRef.current?.focus();
              openPanel();
            }}
          >
            <SearchIcon
              className={twMerge(
                'size-5 shrink-0 transition-colors duration-200 group-hover:text-green-deep02',
                isOpen ? 'text-green-deep02' : 'text-text-placeholder',
              )}
            />
            <input
              ref={inputRef}
              value={query}
              onChange={handleQueryChange}
              onFocus={() => openPanel()}
              placeholder={resolvedPlaceholder}
              className='min-w-0 flex-1 border-none bg-transparent p-0 text-base text-text-primary outline-none placeholder:text-text-placeholder'
            />
            {showClear && (
              <BaseButton
                type='text'
                size='s'
                htmlType='button'
                onClick={handleClearQuery}
                className='size-5 min-w-0 shrink-0 rounded-full border-0 p-0 bg-grayscale-gray02 text-text-placeholder transition-colors hover:bg-grayscale-gray03 hover:text-text-placeholder'
                icon={<CancelIcon className='size-4' />}
              />
            )}
          </div>
        </div>

        {showPanel && (
          <div
            className={twMerge(
              'w-full rounded-[15px] border border-grayscale-gray01 bg-grayscale-gray00 px-6 pb-6 pt-5 shadow-3 transition-all duration-200 ease-out origin-top',
              visible
                ? 'opacity-100 scale-y-100 translate-y-0'
                : 'opacity-0 scale-y-95 -translate-y-1',
            )}
          >
            <div className='flex w-full flex-wrap gap-x-3 gap-y-2'>
              {/* 선택된 칩 (어두운 배경) */}
              {selected.map((label) => (
                <BaseButton
                  key={label}
                  type='text'
                  size='m'
                  htmlType='button'
                  typo={false}
                  isStatic
                  gap='gap-2'
                  iconPosition='after'
                  icon={<CancelIcon className='size-4 text-grayscale-gray01' />}
                  onClick={() => handleRemove(label)}
                  textWrapperClass='h-auto flex-none whitespace-nowrap text-[12px] font-medium leading-[1.4] text-grayscale-gray01'
                  className='h-[34px] min-w-0 cursor-pointer items-center gap-2 rounded-full border-0 bg-grayscale-gray07 py-2 pl-3 pr-2'
                >
                  {label}
                </BaseButton>
              ))}

              {selected.length > 0 &&
                (unselected.length > 0 || showAddAsSchool) && (
                  <div className='my-1 h-px w-full bg-grayscale-gray01' />
                )}

              {/* 검색 결과 칩 — full 상태면 클릭 시 shake */}
              {unselected.map((item) => (
                <BaseButton
                  key={item.code}
                  type='text'
                  size='m'
                  htmlType='button'
                  typo={false}
                  isStatic
                  gap='gap-2'
                  iconPosition='after'
                  icon={<PlusIcon className='size-4 text-grayscale-gray07' />}
                  onClick={() => handleAdd(item.label)}
                  textWrapperClass='h-auto flex-none whitespace-nowrap text-[12px] font-medium leading-[1.4] text-grayscale-gray07'
                  className='h-[34px] min-w-0 cursor-pointer items-center gap-2 rounded-full border-0 bg-grayscale-gray01 py-2 pl-3 pr-2 transition-colors duration-150 hover:bg-grayscale-gray02'
                >
                  {item.label}
                </BaseButton>
              ))}

              {/* "Add as school" 커스텀 옵션 */}
              {showAddAsSchool && (
                <>
                  {(selected.length > 0 || unselected.length > 0) && (
                    <div className='my-1 h-px w-full bg-grayscale-gray01' />
                  )}
                  <BaseButton
                    type='text'
                    size='m'
                    htmlType='button'
                    typo={false}
                    isStatic
                    gap='gap-2'
                    iconPosition='after'
                    icon={<PlusIcon className='size-4 text-grayscale-gray07' />}
                    onClick={() => handleAdd(trimmedQuery)}
                    textWrapperClass='h-auto flex-none whitespace-nowrap text-[12px] font-medium leading-[1.4] text-grayscale-gray07'
                    className='h-[34px] min-w-0 cursor-pointer items-center gap-2 rounded-full border-0 bg-grayscale-gray01 py-2 pl-3 pr-2 transition-colors duration-150 hover:bg-grayscale-gray02'
                  >
                    {t(
                      `Add "${trimmedQuery}" as a school`,
                      `"${trimmedQuery}" 학교로 추가하기`,
                    )}
                  </BaseButton>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PathfinderUniversitySlotSelect;
